import type { RouteRecordNameGeneric, Router, RouteLocationNormalizedLoadedGeneric } from 'vue-router';
import type { IAppRoute } from './router-interface';
import { isNullOrEmpty } from '@powerduck/common/utils/is-null-or-empty';
import { PortalUtils } from '@powerduck/common/utils/utils';
import NProgress from 'nprogress';
import { createRouter, createWebHistory } from 'vue-router';
import { AppConfig } from '../app-config';
import AppRoutes from './../app-routes';
import { AppState } from '../app-state';
import 'nprogress/nprogress.css';

const AppRouterUtils = (() => {
	let initialized = false;
	let currentRouter: Router = null;
	let previousRoute: RouteLocationNormalizedLoadedGeneric = null;
	let nextTo: RouteLocationNormalizedLoadedGeneric = null;

	const getRouteId = (route: AppRoute): string => {
		if (route.name != null && route.name.length > 0) {
			return route.name;
		} else {
			return route.path.split('/')[1];
		}
	};

	const getRouteByName = (name: RouteRecordNameGeneric): AppRoute => {
		for (const key in AppRoutes) {
			const item: AppRoute = AppRoutes[key];
			if (item.name == name) {
				return item;
			}
		}

		return null;
	};

	const getPossibleInitialRoute = (): AppRoute => {
		try {
			const locPath = `/${location.pathname.split('/')[1]}/`;
			for (const key in AppRoutes) {
				const item: AppRoute = AppRoutes[key];
				if (item.path.startsWith(locPath)) {
					return item;
				} else if (`${item.path}/` == locPath) {
					return item;
				} else if (item.path == locPath) {
					return item;
				}
			}

			return null;
		} catch (e) {
			return null;
		}
	};

	const getPreviousRoute = (): IAppRoute => {
		if (previousRoute == null) {
			return null;
		}

		return getRouteByName(previousRoute.name as string);
	};

	const getRoutes = (): AppRoute[] => {
		const retVal = [];
		for (const key in AppRoutes) {
			const route: AppRoute = AppRoutes[key];
			if (route.path.indexOf('/') != 0) {
				console.error(`Route invalid [${route.path}]`);
			}

			retVal.push(AppRoutes[key]);
		}

		return retVal;
	};

	const handlePageSpecificClass = (to, from) => {
		const toRoute: AppRoute = getRouteByName(to.name);
		const clsList = document.body.classList;

		clsList.remove('inv-no-menu');
		clsList.remove('inv-no-header');
		clsList.remove('inv-full-screen');
		clsList.remove('inv-header-visible');
		clsList.remove('inv-layout-buyer');
		clsList.remove('inv-layout-marketplace');

		if (toRoute != null) {
			if (toRoute.hideHeader == true) {
				clsList.add('inv-no-header');
			}

			if (toRoute.hideMenu == true && !AppState.user) {
				clsList.add('inv-no-menu');
			}

			if (toRoute.fullScreen == true) {
				clsList.add('inv-full-screen');
			}

			const layout = toRoute.layout || 'admin';
			if (layout === 'buyer' || layout === 'marketplace') {
				clsList.add('inv-header-visible');
				clsList.add(`inv-layout-${layout}`);
			}
		}

		document.body.classList.remove(`apg-${getRouteId(from)}`);
		document.body.classList.add(`apg-${getRouteId(to)}`);
	};

	const createNewRouter = (): Router => {
		const router = createRouter({
			history: createWebHistory(),
			routes: getRoutes(),
		});

		router.onError((err) => {
			if (err.name == 'ChunkLoadError') {
				const lastTry = Number(localStorage.getItem('errLastReload') || '0');
				if (new Date().getTime() - lastTry > 3000) {
					localStorage.setItem('errLastReload', new Date().getTime().toString());

					if (nextTo != null && !isNullOrEmpty(nextTo.path)) {
						location.href = nextTo.path;
					} else {
						location.reload();
					}
				}
			}
		});

		router.beforeEach((
			to,
			from,
			next,
		) => {
			nextTo = to;
			previousRoute = from;
			handlePageSpecificClass(to, from);

			//Remove any orphaned dropdown clones that were appended to body
			document.querySelectorAll('.dt-dropdown-button-cloned').forEach(el => el.remove());

			next();
		});

		// Not the nicests way that hides menu after router page navigation on mobiles
		router.afterEach((to, from) => {
			NProgress.done();
			PortalUtils.handleMobileMenuClick();
		});

		router.beforeResolve((
			to,
			from,
			next,
		) => {
			// If this isn't an initial page load.
			if (to.name) {
				if (PortalUtils.treatAsMobileDevice()) {
					const loaderContainer = $('.mobileLoader');
					loaderContainer.addClass('mobileLoaderActive').html(AppConfig.blockerHtml);
				}

				NProgress.start();
			}

			next();
		});

		router.afterEach((to, from) => {
			if (PortalUtils.treatAsMobileDevice()) {
				const loaderContainer = $('.mobileLoader');
				loaderContainer.removeClass('mobileLoaderActive').html('');

				setTimeout(() => {
					const loaderContainer = $('.mobileLoader');
					loaderContainer.removeClass('mobileLoaderActive').html('');
				}, 100);
			}
		});

		initialized = true;

		return router;
	};

	const getRouter = (): Router => {
		if (currentRouter == null) {
			currentRouter = createNewRouter();
		}

		return currentRouter;
	};

	return {
		getRouteByName,
		getPossibleInitialRoute,
		getPreviousRoute,
		getRouter,
	};
})();

export interface AppRoute {
	path: string;
	component: any;
	name: string;
	title?: () => string;
	hideMenu?: boolean;
	hideHeader?: boolean;
	fullScreen?: boolean;
	layout?: import('./router-interface').SpaLayout;
}

export class AppRouter {
	static get currentRouter(): Router {
		return AppRouterUtils.getRouter();
	}

	static get previousRoute(): IAppRoute {
		return AppRouterUtils.getPreviousRoute();
	}

	static getRouteByName(name: RouteRecordNameGeneric): AppRoute {
		return AppRouterUtils.getRouteByName(name);
	}

	static getPossibleInitialRoute(): AppRoute {
		return AppRouterUtils.getPossibleInitialRoute();
	}
}
